"use client";

/**
 * CreationViewerModal — the in-app reader for creations that render better in
 * the product than in a browser tab (founder, 08-03: clicking an md creation
 * opened raw source). Hosts the unified FilePreview panel engine, so whatever
 * the Files tab can render (markdown with Preview/Source tabs, csv, code…)
 * renders identically here. Mounted ONCE in AppShell; openCreation routes
 * matching kinds to the store this reads. "Open raw" keeps the old tab path.
 */

import { useEffect } from "react";
import { useCreationViewerStore } from "@/stores/creationViewerStore";
import { FilePreview } from "@/components/files/FilePreview";
import { CloseIcon } from "@/components/icons/CloseIcon";

export function CreationViewerModal() {
  const target = useCreationViewerStore((s) => s.target);
  const close = useCreationViewerStore((s) => s.close);

  useEffect(() => {
    if (!target) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [target, close]);

  if (!target) return null;
  const contentHref = `/v/${target.id}`;

  return (
    <>
      <div
        className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm"
        onClick={close}
        data-testid="creation-viewer-backdrop"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={target.filename}
        className="fixed z-[91] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(92vw,860px)] h-[min(88vh,980px)] flex flex-col rounded-20 overflow-hidden bg-bg-base border border-border-subtle shadow-2xl"
      >
        <div className="flex items-center gap-3 px-5 h-12 border-b border-border-subtle flex-shrink-0">
          <span className="text-3.5 font-medium text-fg-default truncate flex-1">
            {target.filename}
          </span>
          <button
            type="button"
            className="text-3 text-fg-muted hover:text-fg-default transition-colors"
            onClick={() =>
              window.open(contentHref, "_blank", "noopener,noreferrer")
            }
          >
            Open raw
          </button>
          <button
            type="button"
            aria-label="Close"
            className="p-1 rounded-8 text-fg-muted hover:text-fg-default hover:bg-bg-muted transition-colors"
            onClick={close}
          >
            <CloseIcon width={14} height={14} />
          </button>
        </div>
        {/* creation-reader-host lifts the legacy .fo-preview* height caps
            (same fix .fpc-host applies in the Files canvas) while keeping the
            Preview/Source tabs — without it the markdown body clips mid-page. */}
        <div className="creation-reader-host flex-1 min-h-0 overflow-y-auto flex flex-col min-h-0 [&_.fo-preview]:flex-1 [&_.fo-preview]:flex [&_.fo-preview]:flex-col [&_.fo-preview]:min-h-0 [&_.fo-preview]:border-t-0 [&_.fo-preview]:bg-transparent [&_.fo-preview\_\_content]:flex-1 [&_.fo-preview\_\_content]:max-h-none [&_.fo-preview\_\_content]:min-h-0 [&_.fo-preview\_\_iframe]:h-full [&_.fo-preview\_\_iframe]:min-h-0 [&_.fo-preview\_\_markdown]:max-h-none [&_.fo-preview\_\_markdown]:min-h-0 [&_.fo-preview\_\_markdown]:py-7 [&_.fo-preview\_\_markdown]:px-10 [&_.fo-preview\_\_code]:max-h-none [&_.fo-preview\_\_code]:min-h-0 [&_.fo-preview\_\_image]:max-h-none">
          <FilePreview
            size="panel"
            filename={target.filename}
            url={contentHref}
          />
        </div>
      </div>
    </>
  );
}
